/**
 * PERent — Stuhlmiete-Kalkulation als volle Seite (PE-Design).
 * Hauptliste = nur umgelegte BWA-Kosten.
 * Ausgeschlossene Kosten: einklappbar (Transparenz), Zuschalten nur mit PIN PW90.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Lock, Sparkles, Unlock } from 'lucide-react';
import useAppStore, { useCalculationsSelector } from '../store/appStore';
import { usePERentStore, PE_RENT_BWA_KEYS } from '../store/usePERentStore';
import { usePERentCalculation, type PERentLineItem } from '../hooks/usePERentCalculation';
import { PERentPaywallModal } from '../components/perent/PERentPaywallModal';
import {
  PERentContractModal,
  type PERentTenantData,
} from '../components/perent/PERentContractModal';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  formatPremiumCancelAvailableDate,
  isPremiumCancelAllowed,
} from '../utils/premiumCostBridge';
import { formatMoney, resolveCurrencyCode } from '../utils/currencyFormatter';

const BWA_LABELS: Record<string, string> = {
  raumkosten: 'Raumkosten',
  betriebssteuern: 'Betriebliche Steuern',
  versicherungen: 'Versicherungen/Beiträge',
  besondereKosten: 'besondere Kosten',
  kfzKosten: 'KFZ Kosten',
  werbeReisekosten: 'Werbe/Reisekosten',
  warenabgabe: 'Warenabgabe an MA',
  reparatur: 'Reparatur/Instandhaltung',
  sonstigeKosten: 'sonstige Kosten',
  kredite: 'Kredite, Zinsen u.ä.',
  zinsaufwendungen: 'Zinsaufwendungen',
  gewerbesteuer: 'Gewerbesteuer',
  koerperschaftssteuer: 'Körperschaftssteuer (GmbH)',
  budgetPlansumme: 'Budget Plansumme',
  preisExpert: 'PreisExpert Beratung',
  premiumSoftware: 'Premium-Software',
  sonstigeEintrag1: 'sonstige Einträge',
  sonstigeEintrag2: 'sonstige Einträge',
  sonstigeEintrag3: 'sonstige Einträge',
  sonstigeEintrag4: 'sonstige Einträge',
};

const formatHoursWhole = (value: number) =>
  `${new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: 0,
  }).format(Math.round(value || 0))} Std.`;

const formatHours = (value: number) =>
  `${new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value || 0)} Std.`;

/** Eine Dezimalstelle für Stunden-Eingabe (de-DE) */
const formatHoursInput = (value: number) =>
  Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

/** Dezimal mit Komma für Geld-Input-Anzeige (de-DE) */
const formatDecimalInput = (value: number) =>
  Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PERentPage: React.FC = () => {
  const openPremiumModal = useAppStore((s) => s.openPremiumModal);
  const unlockedPremiumTools = useAppStore((s) => s.unlockedPremiumTools);
  const clientData = useAppStore((s) => s.clientData);
  const settings = useAppStore((s) => s.settings);
  const calc = useCalculationsSelector();

  const currencyCode = resolveCurrencyCode(
    calc?.currencyCode || settings?.country?.currency,
    settings?.country?.activeLand || settings?.country?.land
  );

  const formatMoney2 = (value: number) => formatMoney(value || 0, currencyCode, 2);
  const formatMoneyWhole = (value: number) =>
    formatMoney(Math.round(value || 0), currencyCode, 0);

  const {
    isLicensed,
    isPinUnlocked,
    verifyPin,
    lockPin,
    includedBwaKeys,
    toggleBwaKey,
    manualCosts,
    addManualCost,
    updateManualCost,
    toggleManualCost,
    toggleManualDeduction,
    removeManualCost,
    hourlyFactorOverride,
    setHourlyFactorOverride,
    hoursInputMode,
    setHoursInputMode,
    tenantMonthlyHours,
    tenantWeeklyHours,
    setTenantMonthlyHours,
    setTenantWeeklyHours,
    cancelLicense,
  } = usePERentStore();

  const [paywallOpen, setPaywallOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [tenantData, setTenantData] = useState<PERentTenantData>({
    name: '',
    tradeName: '',
    address: '',
    taxNumber: '',
    vatId: '',
    email: '',
    phone: '',
  });
  const [excludedOpen, setExcludedOpen] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const [manualEuro, setManualEuro] = useState('');
  const [manualIsDeduction, setManualIsDeduction] = useState(false);
  const [factorDraft, setFactorDraft] = useState('');
  const [weeklyHoursDraft, setWeeklyHoursDraft] = useState('');
  const [monthlyHoursDraft, setMonthlyHoursDraft] = useState('');
  const [confirmFactorOpen, setConfirmFactorOpen] = useState(false);
  const [pendingFactor, setPendingFactor] = useState<number | null>(null);

  useEffect(() => {
    if (unlockedPremiumTools.includes('pe-rent') && !isLicensed) {
      usePERentStore.setState({ isLicensed: true, licenseType: 'sub_24' });
    }
  }, [unlockedPremiumTools, isLicensed]);

  const bwaData = useMemo(
    () => (clientData?.bwaData || {}) as Record<string, unknown>,
    [clientData?.bwaData]
  );
  const zeitraum = Number(bwaData.zeitraum) > 0 ? Number(bwaData.zeitraum) : 12;
  const geraeteCount = Math.max(0, Number(bwaData.visibleGeraeteCount) || 10);

  const allLines: PERentLineItem[] = useMemo(() => {
    const bwaLines = PE_RENT_BWA_KEYS.map((key) => {
      const labelKey = `${key}_label`;
      const customLabel =
        key.startsWith('sonstigeEintrag') && bwaData[labelKey]
          ? String(bwaData[labelKey])
          : null;
      const yearly = Number(bwaData[key]) || 0;
      const monthlyEuro = Number((yearly / zeitraum).toFixed(2));
      return {
        key,
        label: customLabel || BWA_LABELS[key] || key,
        monthlyEuro,
        included: !!includedBwaKeys[key],
      };
    });

    const geraeteLines: PERentLineItem[] = [];
    for (let i = 1; i <= geraeteCount; i++) {
      const key = `geraetEintrag${i}`;
      const labelKey = `${key}_label`;
      const yearly = Number(bwaData[key]) || 0;
      if (yearly === 0) continue;
      geraeteLines.push({
        key,
        label: String(bwaData[labelKey] || `Gerät ${i}`),
        monthlyEuro: Number((yearly / zeitraum).toFixed(2)),
        included: !!includedBwaKeys[key],
      });
    }

    const manualLines: PERentLineItem[] = manualCosts.map((c) => {
      const amount = Math.abs(Number(c.monthlyEuro) || 0);
      const signed = c.isDeduction ? -amount : amount;
      return {
        key: c.id,
        label: c.label || (c.isDeduction ? 'Abzug' : 'Manuelle Kosten'),
        monthlyEuro: signed,
        included: !!c.included,
        isDeduction: !!c.isDeduction,
      };
    });

    return [...bwaLines, ...geraeteLines, ...manualLines].filter(
      (line) => line.monthlyEuro !== 0
    );
  }, [bwaData, zeitraum, includedBwaKeys, geraeteCount, manualCosts]);

  const includedLines = allLines.filter((l) => l.included);
  const excludedLines = allLines.filter((l) => !l.included);
  const isManualKey = (key: string) => key.startsWith('manual_');

  const handleToggleLine = (key: string) => {
    if (isManualKey(key)) toggleManualCost(key);
    else toggleBwaKey(key);
  };

  const handleAddManual = (e: React.FormEvent) => {
    e.preventDefault();
    const euro = Number(String(manualEuro).replace(',', '.'));
    if (!manualLabel.trim() || !(euro > 0)) return;
    addManualCost(manualLabel.trim(), euro, manualIsDeduction);
    setManualLabel('');
    setManualEuro('');
    setManualIsDeduction(false);
  };

  const salonTeamHours = Number(calc?.summeStundenTeam) || 0;
  const result = usePERentCalculation({
    lines: allLines,
    salonTeamHoursMonthly: salonTeamHours,
  });

  useEffect(() => {
    setWeeklyHoursDraft(
      tenantWeeklyHours > 0 ? formatHoursInput(tenantWeeklyHours) : ''
    );
  }, [tenantWeeklyHours]);

  useEffect(() => {
    setMonthlyHoursDraft(
      tenantMonthlyHours > 0 ? formatHoursInput(tenantMonthlyHours) : ''
    );
  }, [tenantMonthlyHours]);

  useEffect(() => {
    if (!result.hasTenantHours) {
      setFactorDraft('');
      return;
    }
    const raw = result.isHourlyFactorOverridden
      ? Number(hourlyFactorOverride ?? 0)
      : Number(result.calculatedHourlyFactor || 0);
    setFactorDraft(formatDecimalInput(raw));
  }, [
    result.calculatedHourlyFactor,
    result.isHourlyFactorOverridden,
    result.hasTenantHours,
    hourlyFactorOverride,
  ]);

  const peRentActivatedAt =
    (clientData as { premiumToolActivatedAt?: Record<string, string> } | null)
      ?.premiumToolActivatedAt?.['pe-rent'] || null;
  const canCancel = isPremiumCancelAllowed(peRentActivatedAt);
  const unlocked = unlockedPremiumTools.includes('pe-rent') || isLicensed;

  const cd = clientData as {
    salonname?: string;
    firmenname?: string;
    strasse?: string;
    plz?: string;
    ort?: string;
    inhaber?: string;
    vorname?: string;
    nachname?: string;
    steuernummer?: string;
    ustIdNr?: string;
    ustid?: string;
    iban?: string;
  } | null;

  const salonName =
    String(cd?.salonname || cd?.firmenname || 'Salon') || 'Salon';
  const salonAddress = [
    cd?.strasse,
    [cd?.plz, cd?.ort].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  const salonOwnerName =
    cd?.inhaber ||
    [cd?.vorname, cd?.nachname].filter(Boolean).join(' ') ||
    undefined;
  const salonData = {
    salonName,
    ownerName: salonOwnerName,
    address: salonAddress || undefined,
    taxNumber: cd?.steuernummer || undefined,
    vatId: cd?.ustIdNr || cd?.ustid || undefined,
    iban: cd?.iban || undefined,
  };
  const updateTenantData = (patch: Partial<PERentTenantData>) =>
    setTenantData((prev) => ({ ...prev, ...patch }));

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const ok = verifyPin(pinInput.trim());
    setPinError(!ok);
    if (ok) setPinInput('');
  };

  const commitWeeklyHours = () => {
    const n = Number(String(weeklyHoursDraft).replace(',', '.'));
    if (Number.isNaN(n) || n < 0) {
      setWeeklyHoursDraft(tenantWeeklyHours > 0 ? formatHoursInput(tenantWeeklyHours) : '');
      return;
    }
    setTenantWeeklyHours(n);
    setWeeklyHoursDraft(n > 0 ? formatHoursInput(n) : '');
  };

  const commitMonthlyHours = () => {
    const n = Number(String(monthlyHoursDraft).replace(',', '.'));
    if (Number.isNaN(n) || n < 0) {
      setMonthlyHoursDraft(tenantMonthlyHours > 0 ? formatHoursInput(tenantMonthlyHours) : '');
      return;
    }
    setTenantMonthlyHours(n);
    setMonthlyHoursDraft(n > 0 ? formatHoursInput(n) : '');
  };

  const requestFactorChange = () => {
    if (!result.hasTenantHours) return;
    const raw = Number(String(factorDraft).replace(',', '.'));
    if (Number.isNaN(raw) || raw < 0) return;
    const next = Number(raw.toFixed(2));
    // 0 oder gleich dem Kalkulationswert → Override löschen, echter Satz gilt wieder
    if (next <= 0 || Math.abs(next - result.calculatedHourlyFactor) < 0.005) {
      setHourlyFactorOverride(null);
      setFactorDraft(formatDecimalInput(result.calculatedHourlyFactor || 0));
      return;
    }
    setPendingFactor(next);
    setConfirmFactorOpen(true);
  };

  const applyHoursAndFactorOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (hoursInputMode === 'week') commitWeeklyHours();
    else commitMonthlyHours();
    requestFactorChange();
  };

  const confirmFactorChange = () => {
    if (pendingFactor !== null && pendingFactor > 0) {
      setHourlyFactorOverride(pendingFactor);
    } else {
      setHourlyFactorOverride(null);
      setFactorDraft(formatDecimalInput(result.calculatedHourlyFactor || 0));
    }
    setConfirmFactorOpen(false);
    setPendingFactor(null);
  };

  const resetFactorToCalculated = () => {
    setHourlyFactorOverride(null);
    setFactorDraft(formatDecimalInput(result.calculatedHourlyFactor || 0));
  };

  return (
    <div className="min-h-screen bg-pe-bg dark:bg-[#030303] px-4 py-6 md:px-8 pb-16">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
              Premium · Kalkulation
            </p>
            <h1 className="text-3xl font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">
              <span className="italic font-normal">PE</span>Rent
            </h1>
            <p className="mt-2 max-w-2xl text-base text-stone-600 dark:text-slate-400 leading-relaxed">
              Stuhlmiete aus den umlegbaren BWA-Kosten. Ausgeschlossene Positionen siehst du
              eingeklappt zur Transparenz — Zuschalten nur mit Admin-Passwort.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <button
              type="button"
              onClick={() => openPremiumModal('pe-rent')}
              className="rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
            >
              Tarifübersicht
            </button>
            {!unlocked ? (
              <button
                type="button"
                onClick={() => setPaywallOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#E6C299] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
              >
                <Sparkles className="w-4 h-4" />
                Freischalten
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setIsContractModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-pe-border dark:border-[#333] bg-pe-card dark:bg-[#141414] px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-pe-text dark:text-white hover:border-[#E6C299]/50"
                >
                  <FileText className="w-4 h-4 text-[#E6C299]" />
                  Mietvertrag erstellen
                </button>
                {canCancel ? (
                  <button
                    type="button"
                    onClick={() => cancelLicense()}
                    className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-rose-400"
                  >
                    Abo kündigen
                  </button>
                ) : (
                  <span className="self-center text-sm text-stone-500 dark:text-slate-500">
                    Kündigung ab {formatPremiumCancelAvailableDate(peRentActivatedAt)}
                  </span>
                )}
              </>
            )}
          </div>
        </header>

        {/* KPI — groß & klar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] p-5">
            <p className="text-base font-semibold text-stone-500 dark:text-slate-400">
              Umzulegende Kostenbasis
            </p>
            <p className="mt-2 text-3xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
              {formatMoneyWhole(result.rentBaseMonthly)}
            </p>
            <p className="mt-2 text-base text-stone-500 dark:text-slate-500">pro Monat</p>
          </div>
          <div className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] p-5">
            <p className="text-base font-semibold text-stone-500 dark:text-slate-400">Stundenfaktor</p>
            {result.hasTenantHours ? (
              <>
                <p className="mt-2 text-3xl font-black tabular-nums text-pe-text dark:text-white">
                  {formatMoney2(result.hourlyFactor)}
                </p>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500 leading-snug">
                  Aktive Teamstunden (Chef + produktive MA): {formatHoursWhole(result.salonHours)}
                  {' + '}
                  Mieter {formatHours(result.tenantHours)}
                </p>
                {result.isHourlyFactorOverridden ? (
                  <p className="mt-1.5 text-sm font-semibold text-amber-600 dark:text-amber-400">
                    Angebot · kalkuliert wäre {formatMoney2(result.calculatedHourlyFactor)}
                  </p>
                ) : (
                  <p className="mt-1.5 text-sm text-stone-500 dark:text-slate-500">
                    Kosten ÷ (Team + Mieter)
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="mt-2 text-3xl font-black tabular-nums text-stone-500">—</p>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500 leading-snug">
                  Erscheint, sobald Mieterstunden eingetragen sind
                </p>
              </>
            )}
          </div>
          <div className="rounded-2xl border border-[#E6C299]/50 bg-[#E6C299]/15 p-5">
            <p className="text-base font-semibold text-pe-accent-text dark:text-[#E6C299]">
              Empfohlene Stuhlmiete
            </p>
            {result.hasTenantHours ? (
              <>
                <p className="mt-2 text-3xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                  {formatMoney2(result.rentNet)}
                </p>
                <p className="mt-2 text-base text-stone-600 dark:text-slate-400">
                  netto / Monat · brutto {formatMoney2(result.rentGross)}
                  {result.isHourlyFactorOverridden ? ' · Angebot' : ''}
                </p>
              </>
            ) : (
              <>
                <p className="mt-2 text-3xl font-black tabular-nums text-stone-500">—</p>
                <p className="mt-2 text-base text-stone-600 dark:text-slate-400">
                  erst nach Mieterstunden
                </p>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6">
          {/* Linke Spalte: Kosten */}
          <div className="space-y-4">
            <section className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] p-5 md:p-6">
              <h2 className="text-xl font-black uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                Umgelegte BWA-Kosten
              </h2>
              <p className="mt-2 text-base text-stone-600 dark:text-slate-400 leading-relaxed">
                Diese Positionen fließen in die Stuhlmiete ein (Bezeichnungen wie in der
                Kostenstruktur).
              </p>

              <ul className="mt-5 divide-y divide-pe-border dark:divide-[#222] rounded-xl border border-pe-border dark:border-[#2a2a2a] overflow-hidden">
                {includedLines.length === 0 ? (
                  <li className="px-4 py-5 text-base text-stone-500">
                    Keine umgelegten Kosten aktiv. Prüfe die BWA oder schalte Positionen per Admin-PIN
                    zu.
                  </li>
                ) : (
                  includedLines.map((line) => (
                    <li
                      key={line.key}
                      className={`flex items-center justify-between gap-4 px-4 py-4 ${
                        line.isDeduction ? 'bg-rose-500/10' : 'bg-[#E6C299]/5'
                      }`}
                    >
                      <span className="text-lg font-semibold text-pe-text dark:text-white">
                        {line.isDeduction ? (
                          <span className="text-rose-400 mr-2 text-sm font-bold uppercase tracking-wider">
                            Abzug
                          </span>
                        ) : null}
                        {line.label}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-lg font-black tabular-nums ${
                            line.isDeduction
                              ? 'text-rose-400'
                              : 'text-pe-text dark:text-white'
                          }`}
                        >
                          {formatMoney2(line.monthlyEuro)}
                        </span>
                        {isPinUnlocked && (
                          <button
                            type="button"
                            onClick={() => handleToggleLine(line.key)}
                            className="text-sm font-bold text-rose-400 hover:underline print:hidden"
                          >
                            Ausnehmen
                          </button>
                        )}
                        {isPinUnlocked && isManualKey(line.key) && (
                          <button
                            type="button"
                            onClick={() => removeManualCost(line.key)}
                            className="text-sm font-bold text-stone-500 hover:text-rose-400 hover:underline print:hidden"
                          >
                            Löschen
                          </button>
                        )}
                      </div>
                    </li>
                  ))
                )}
                <li className="flex items-center justify-between gap-4 px-4 py-4 bg-pe-elevated dark:bg-[#141414]">
                  <span className="text-lg font-black text-pe-accent-text dark:text-[#E6C299]">
                    Summe umgelegt
                  </span>
                  <span className="text-xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                    {formatMoneyWhole(result.rentBaseMonthly)}
                  </span>
                </li>
              </ul>
            </section>

            {/* Ausgeschlossene Kosten — Accordion */}
            <section className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] overflow-hidden">
              <button
                type="button"
                onClick={() => setExcludedOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 text-left hover:bg-pe-elevated/50 dark:hover:bg-[#141414]/80 print:hidden"
              >
                <div>
                  <h2 className="text-lg font-black uppercase tracking-wider text-stone-600 dark:text-slate-300">
                    Ausgeschlossene Kosten
                  </h2>
                  <p className="mt-1 text-base text-stone-500 dark:text-slate-500">
                    {excludedLines.length} Positionen · {formatMoney2(result.excludedMonthly)} / Monat —
                    nur zur Transparenz
                  </p>
                </div>
                {excludedOpen ? (
                  <ChevronUp className="w-6 h-6 text-[#E6C299] shrink-0" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-[#E6C299] shrink-0" />
                )}
              </button>

              {excludedOpen && (
                <div className="border-t border-pe-border dark:border-[#222] px-5 md:px-6 py-5 space-y-4">
                  <p className="text-base text-stone-600 dark:text-slate-400 leading-relaxed">
                    Diese BWA-Kosten werden standardmäßig <strong>nicht</strong> auf die Stuhlmiete
                    umgelegt. Zuschalten nur nach Admin-Freigabe (Passwort).
                  </p>

                  {/* PIN */}
                  <div className="rounded-xl border border-pe-border dark:border-[#333] bg-pe-elevated dark:bg-[#141414] p-4 print:hidden">
                    {isPinUnlocked ? (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="inline-flex items-center gap-2 text-base font-semibold text-emerald-500">
                          <Unlock className="w-5 h-5" />
                          Admin-Modus aktiv — Kosten können zugeschaltet werden
                        </p>
                        <button
                          type="button"
                          onClick={() => lockPin()}
                          className="text-sm font-bold uppercase tracking-wider text-stone-500 hover:text-pe-text"
                        >
                          Sperren
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handlePinSubmit} className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[12rem]">
                          <label className="flex items-center gap-2 text-base font-semibold text-stone-600 dark:text-slate-400 mb-1.5">
                            <Lock className="w-4 h-4" />
                            Admin-Passwort zum Zuschalten
                          </label>
                          <input
                            type="password"
                            value={pinInput}
                            onChange={(e) => {
                              setPinInput(e.target.value);
                              setPinError(false);
                            }}
                            placeholder="Passwort"
                            className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] px-4 py-3 text-base text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                          />
                          {pinError && (
                            <p className="mt-1.5 text-sm text-rose-400">Falsches Passwort.</p>
                          )}
                        </div>
                        <button
                          type="submit"
                          className="rounded-xl bg-[#E6C299] px-5 py-3 text-sm font-bold uppercase tracking-wider text-black"
                        >
                          Freigeben
                        </button>
                      </form>
                    )}
                  </div>

                  {isPinUnlocked && (
                    <form
                      onSubmit={handleAddManual}
                      className="rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/10 p-4 space-y-3 print:hidden"
                    >
                      <p className="text-base font-bold text-pe-accent-text dark:text-[#E6C299]">
                        Manuelle Position (Admin)
                      </p>
                      <p className="text-sm text-stone-600 dark:text-slate-400">
                        Zuschlag oder Abzug (z.&nbsp;B. Seminare/Schulungen aus sonstige Kosten
                        herausrechnen). Häkchen „Abzug“ = wird von der Basis abgezogen.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_auto] gap-3">
                        <input
                          type="text"
                          value={manualLabel}
                          onChange={(e) => setManualLabel(e.target.value)}
                          placeholder={
                            manualIsDeduction
                              ? 'z. B. Seminare / Schulungen'
                              : 'Bezeichnung, z. B. Stromanteil'
                          }
                          className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] px-4 py-3 text-base text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                        />
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={manualEuro}
                          onChange={(e) => setManualEuro(e.target.value)}
                          placeholder={`${currencyCode} / Monat`}
                          className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] px-4 py-3 text-base font-bold text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                        />
                        <button
                          type="submit"
                          className="rounded-xl bg-[#E6C299] px-5 py-3 text-sm font-bold uppercase tracking-wider text-black"
                        >
                          Hinzufügen
                        </button>
                      </div>
                      <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={manualIsDeduction}
                          onChange={(e) => setManualIsDeduction(e.target.checked)}
                          className="w-5 h-5 accent-[#E6C299] cursor-pointer"
                        />
                        <span className="text-sm font-bold text-pe-text dark:text-white">
                          Als Abzug verbuchen (reduziert die Kostenbasis)
                        </span>
                      </label>
                      {manualCosts.length > 0 && (
                        <ul className="divide-y divide-pe-border dark:divide-[#333] rounded-xl border border-pe-border dark:border-[#333] overflow-hidden">
                          {manualCosts.map((c) => (
                            <li
                              key={c.id}
                              className="flex flex-wrap items-center gap-3 px-3 py-3 bg-pe-card dark:bg-[#0f0f0f]"
                            >
                              <input
                                type="text"
                                value={c.label}
                                onChange={(e) =>
                                  updateManualCost(c.id, { label: e.target.value })
                                }
                                className="flex-1 min-w-[10rem] rounded-lg border border-pe-border dark:border-[#333] bg-transparent px-3 py-2 text-base text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                              />
                              <input
                                type="number"
                                min={0}
                                step={1}
                                value={c.monthlyEuro || ''}
                                onChange={(e) =>
                                  updateManualCost(c.id, {
                                    monthlyEuro: Number(e.target.value),
                                  })
                                }
                                className="w-28 rounded-lg border border-pe-border dark:border-[#333] bg-transparent px-3 py-2 text-base font-bold tabular-nums text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                              />
                              <span className="text-sm text-stone-500">{currencyCode} / Monat</span>
                              <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
                                <input
                                  type="checkbox"
                                  checked={!!c.isDeduction}
                                  onChange={() => toggleManualDeduction(c.id)}
                                  className="w-4 h-4 accent-rose-400 cursor-pointer"
                                />
                                <span
                                  className={`text-sm font-bold ${
                                    c.isDeduction ? 'text-rose-400' : 'text-stone-500'
                                  }`}
                                >
                                  Abzug
                                </span>
                              </label>
                              <button
                                type="button"
                                onClick={() => removeManualCost(c.id)}
                                className="text-sm font-bold text-rose-400 hover:underline"
                              >
                                Löschen
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </form>
                  )}

                  <ul className="divide-y divide-pe-border dark:divide-[#222] rounded-xl border border-pe-border dark:border-[#2a2a2a] overflow-hidden">
                    {excludedLines.length === 0 ? (
                      <li className="px-4 py-4 text-base text-stone-500">
                        Keine weiteren BWA-Betriebskosten mit Betrag vorhanden.
                      </li>
                    ) : (
                      excludedLines.map((line) => (
                        <li
                          key={line.key}
                          className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 opacity-90"
                        >
                          <div>
                            <p className="text-lg font-semibold text-stone-600 dark:text-slate-300">
                              {line.label}
                            </p>
                            <p className="text-sm text-stone-500 mt-0.5">Nicht in der Basis</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold tabular-nums text-stone-500 dark:text-slate-400">
                              {formatMoney2(line.monthlyEuro)}
                            </span>
                            {isPinUnlocked && (
                              <button
                                type="button"
                                onClick={() => handleToggleLine(line.key)}
                                className="rounded-lg border border-[#E6C299]/50 bg-[#E6C299]/15 px-3 py-2 text-sm font-bold text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 print:hidden"
                              >
                                + Zuschalten ({formatMoney2(line.monthlyEuro)})
                              </button>
                            )}
                            {isPinUnlocked && isManualKey(line.key) && (
                              <button
                                type="button"
                                onClick={() => removeManualCost(line.key)}
                                className="text-sm font-bold text-stone-500 hover:underline print:hidden"
                              >
                                Löschen
                              </button>
                            )}
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </section>
          </div>

          {/* Rechte Spalte */}
          <div className="space-y-4">
            <section className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] p-5 md:p-6">
              <h2 className="text-xl font-black uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                Stunden des Mieters
              </h2>
              <p className="mt-2 text-base text-stone-600 dark:text-slate-400 leading-relaxed">
                Zuerst Mieterstunden eintragen — danach erscheint der Stundenfaktor (Kosten ÷ Team +
                Mieter). Manuelles Anpassen des Satzes erst danach möglich.
              </p>

              <div className="mt-4 flex gap-2 print:hidden">
                <button
                  type="button"
                  onClick={() => setHoursInputMode('week')}
                  className={`flex-1 rounded-xl px-3 py-3 text-sm font-bold uppercase tracking-wider ${
                    hoursInputMode === 'week'
                      ? 'bg-[#E6C299] text-black'
                      : 'border border-pe-border dark:border-[#333] text-stone-500'
                  }`}
                >
                  Pro Woche
                </button>
                <button
                  type="button"
                  onClick={() => setHoursInputMode('month')}
                  className={`flex-1 rounded-xl px-3 py-3 text-sm font-bold uppercase tracking-wider ${
                    hoursInputMode === 'month'
                      ? 'bg-[#E6C299] text-black'
                      : 'border border-pe-border dark:border-[#333] text-stone-500'
                  }`}
                >
                  Pro Monat
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {hoursInputMode === 'week' ? (
                  <div>
                    <label className="block text-base font-semibold text-stone-600 dark:text-slate-400 mb-2">
                      Mieterstunden / Woche
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={weeklyHoursDraft}
                        onChange={(e) => setWeeklyHoursDraft(e.target.value)}
                        onBlur={commitWeeklyHours}
                        onKeyDown={applyHoursAndFactorOnEnter}
                        onFocus={(e) => e.target.select()}
                        placeholder="z. B. 20,0"
                        className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-elevated dark:bg-[#141414] pl-4 pr-14 py-3.5 text-xl font-bold tabular-nums text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold tracking-wider text-stone-500 dark:text-slate-500">
                        Std.
                      </span>
                    </div>
                    <p className="mt-2 text-base text-stone-500">
                      = {formatHours(tenantMonthlyHours)} / Monat (× 4,33)
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-base font-semibold text-stone-600 dark:text-slate-400 mb-2">
                      Mieterstunden / Monat
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={monthlyHoursDraft}
                        onChange={(e) => setMonthlyHoursDraft(e.target.value)}
                        onBlur={commitMonthlyHours}
                        onKeyDown={applyHoursAndFactorOnEnter}
                        onFocus={(e) => e.target.select()}
                        placeholder="z. B. 80,0"
                        className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-elevated dark:bg-[#141414] pl-4 pr-14 py-3.5 text-xl font-bold tabular-nums text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold tracking-wider text-stone-500 dark:text-slate-500">
                        Std.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {result.hasTenantHours ? (
                <div className="mt-5 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4 space-y-3">
                  <div>
                    <p className="text-base font-bold text-pe-text dark:text-white">
                      Stundensatz anpassen (Angebot)
                    </p>
                    <p className="mt-1 text-sm text-stone-500 dark:text-slate-500 leading-relaxed">
                      Kalkuliert: {formatMoney2(result.calculatedHourlyFactor)} / Std. — du kannst
                      senken oder erhöhen (z.&nbsp;B. Entgegenkommen an den Mieter).
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[10rem]">
                      <label className="block text-sm font-semibold text-stone-600 dark:text-slate-400 mb-1.5">
                        {currencyCode} / Stunde
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={factorDraft}
                          onChange={(e) => setFactorDraft(e.target.value)}
                          onBlur={() => {
                            const n = Number(String(factorDraft).replace(',', '.'));
                            if (!Number.isNaN(n) && n >= 0) {
                              setFactorDraft(formatDecimalInput(n));
                            }
                          }}
                          onKeyDown={applyHoursAndFactorOnEnter}
                          onFocus={(e) => e.target.select()}
                          placeholder="0,00"
                          className="w-full rounded-xl border border-pe-border dark:border-[#333] bg-pe-card dark:bg-[#0f0f0f] pl-4 pr-14 py-3 text-xl font-bold tabular-nums text-pe-text dark:text-white focus:border-[#E6C299] focus:outline-none"
                        />
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold tracking-wider text-stone-500 dark:text-slate-500">
                          {currencyCode}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={requestFactorChange}
                      className="rounded-xl bg-[#E6C299] px-5 py-3 text-sm font-bold uppercase tracking-wider text-black"
                    >
                      Übernehmen
                    </button>
                    {result.isHourlyFactorOverridden && (
                      <button
                        type="button"
                        onClick={resetFactorToCalculated}
                        className="rounded-xl border border-pe-border dark:border-[#333] px-4 py-3 text-sm font-bold text-stone-500 hover:text-pe-text"
                      >
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                  {result.isHourlyFactorOverridden && (
                    <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                      Angebot aktiv: Miete weicht vom kalkulierten Satz ab
                      {result.calculatedRentNet > 0
                        ? ` (kalkuliert wären ${formatMoney2(result.calculatedRentNet)} netto)`
                        : ''}
                      .
                    </p>
                  )}
                </div>
              ) : (
                <p className="mt-5 text-base text-stone-500 dark:text-slate-500">
                  Manuelle Satz-Anpassung wird freigeschaltet, sobald Mieterstunden eingetragen
                  sind.
                </p>
              )}
            </section>

            {/* Ergebnis — klar, große Typo */}
            <section className="rounded-2xl border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] p-5 md:p-6">
              <h2 className="text-xl font-black uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299]">
                Ergebnis
              </h2>

              <div className="mt-5 space-y-4">
                <div className="flex justify-between items-baseline gap-4 text-lg">
                  <span className="text-stone-600 dark:text-slate-400">Kostenbasis</span>
                  <span className="font-bold tabular-nums text-pe-text dark:text-white">
                    {formatMoneyWhole(result.rentBaseMonthly)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline gap-4 text-lg">
                  <span className="text-stone-600 dark:text-slate-400">
                    Teamstunden (aktiv)
                  </span>
                  <span className="font-bold tabular-nums text-pe-text dark:text-white">
                    {formatHoursWhole(result.salonHours)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline gap-4 text-lg">
                  <span className="text-stone-600 dark:text-slate-400">Mieterstunden</span>
                  <span className="font-bold tabular-nums text-pe-text dark:text-white">
                    {result.hasTenantHours ? formatHours(result.tenantHours) : '—'}
                  </span>
                </div>
                {result.hasTenantHours && (
                  <>
                    <div className="flex justify-between items-baseline gap-4 text-lg">
                      <span className="text-stone-600 dark:text-slate-400">
                        Stunden gesamt (Team + Mieter)
                      </span>
                      <span className="font-bold tabular-nums text-pe-text dark:text-white">
                        {formatHours(result.totalHours)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline gap-4 text-lg">
                      <span className="text-stone-600 dark:text-slate-400">
                        Stundenfaktor kalkuliert
                      </span>
                      <span className="font-bold tabular-nums text-pe-text dark:text-white">
                        {formatMoney2(result.calculatedHourlyFactor)} / Std.
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline gap-4 text-lg">
                      <span className="text-stone-600 dark:text-slate-400">
                        {result.isHourlyFactorOverridden
                          ? 'Stundenfaktor Angebot'
                          : 'Stundenfaktor (aktiv)'}
                      </span>
                      <span
                        className={`font-bold tabular-nums ${
                          result.isHourlyFactorOverridden
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-pe-text dark:text-white'
                        }`}
                      >
                        {formatMoney2(result.hourlyFactor)} / Std.
                      </span>
                    </div>
                  </>
                )}

                <div className="rounded-xl bg-[#E6C299]/15 border border-[#E6C299]/40 px-4 py-5 mt-2">
                  <p className="text-base font-semibold text-pe-accent-text dark:text-[#E6C299]">
                    {result.isHourlyFactorOverridden
                      ? 'Angebots-Miete netto / Monat'
                      : 'Empfohlene Miete netto / Monat'}
                  </p>
                  {result.hasTenantHours ? (
                    <>
                      <p className="mt-2 text-4xl font-black tabular-nums text-pe-accent-text dark:text-[#E6C299]">
                        {formatMoney2(result.rentNet)}
                      </p>
                      <p className="mt-2 text-lg text-stone-600 dark:text-slate-400">
                        Brutto (19&nbsp;%): {formatMoney2(result.rentGross)}
                      </p>
                    </>
                  ) : (
                    <p className="mt-2 text-2xl font-black text-stone-500">—</p>
                  )}
                  {!result.hasTenantHours && (
                    <p className="mt-3 text-base font-semibold text-amber-600 dark:text-amber-400">
                      Bitte Mieterstunden eintragen.
                    </p>
                  )}
                </div>

                <p className="text-base text-stone-500 dark:text-slate-500 leading-relaxed pt-1">
                  {result.hasTenantHours
                    ? `Rechnung: Kostenbasis ÷ (Team + Mieter) × Mieterstunden${
                        result.isHourlyFactorOverridden
                          ? ' — Angebot nutzt den angepassten Stundensatz.'
                          : ''
                      }`
                    : 'Stundenfaktor und Miete erscheinen nach Eintrag der Mieterstunden.'}
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <PERentPaywallModal isOpen={paywallOpen} onClose={() => setPaywallOpen(false)} />
      <PERentContractModal
        isOpen={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
        salonData={salonData}
        tenantData={tenantData}
        onUpdateTenantData={updateTenantData}
        rentNet={result.rentNet}
        rentGross={result.rentGross}
        monthlyHours={result.tenantHours || tenantMonthlyHours}
        weeklyHours={tenantWeeklyHours}
        hourlyFactor={result.hourlyFactor}
      />
      <ConfirmationModal
        isOpen={confirmFactorOpen}
        onClose={() => {
          setConfirmFactorOpen(false);
          setPendingFactor(null);
        }}
        onConfirm={confirmFactorChange}
        title="Stundensatz ändern?"
        message={
          <span>
            Wirklich auf{' '}
            <strong className="text-pe-accent-text dark:text-[#E6C299]">
              {formatMoney2(pendingFactor ?? 0)} / Std.
            </strong>{' '}
            setzen?
            <br />
            <span className="mt-2 block text-stone-500">
              Kalkulierter Satz bleibt sichtbar:{' '}
              {formatMoney2(result.calculatedHourlyFactor)} / Std.
            </span>
          </span>
        }
        confirmText="Ja, ändern"
        cancelText="Abbrechen"
      />
    </div>
  );
};

export default PERentPage;
